import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSupportReports, getUserSupportReports, updateSupportReport } from '../api/supportApi';
import { useAuth } from '../contexts/AuthContext';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';
import MainLayout from '../components/layout/MainLayout';
import SupportFormModal from '../components/support/SupportFormModal';
import { ROUTES } from '../utils/constants';
import {
  canEditReleaseNotes,
  canFileSupportReport,
  canViewGlobalSupportQueue,
  defaultSupportView,
  supportCategoryLabel,
  supportEmptyMessage,
  supportQueueTitle,
  supportStatusLabel,
  supportStatusOptions,
  supportViews,
} from '../utils/supportAccess';

const SupportReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewFilter, setViewFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { userData, userClaims } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const role = userData?.role || userClaims?.role;
  const dealerActive = Boolean(dealerAdmin.active);
  const views = supportViews({ role, dealerActive });
  const requestedView = searchParams.get('tab');
  const activeView = views.some((view) => view.id === requestedView)
    ? requestedView
    : defaultSupportView(role, { dealerActive });
  const activeMeta = views.find((view) => view.id === activeView) || views[0];
  const queueTitle = supportQueueTitle(role);
  const showReleaseNotes = canEditReleaseNotes(role);
  const showSignalBridgeFilter = activeView === 'platform' && canViewGlobalSupportQueue(role);
  const canFile = canFileSupportReport(role, { dealerActive });
  const canManageQueue = activeView !== 'mine';

  const statusOptions = supportStatusOptions(true);

  const typeOptions = [
    { value: '', label: 'All Categories' },
    { value: 'question', label: 'Question' },
    { value: 'bug', label: 'Bug' },
    { value: 'account', label: 'Account' },
    { value: 'devices', label: 'Devices' },
    { value: 'billing', label: 'Billing' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'feature', label: 'Feature request' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = activeView === 'mine'
          ? await getUserSupportReports(statusFilter || null, typeFilter || null)
          : await getSupportReports({
            status: statusFilter || null,
            type: typeFilter || null,
            view: activeView === 'platform' ? (viewFilter || null) : null,
          });
        if (cancelled) return;
        setReports(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching reports:', err);
        setReports([]);
        const message = activeView === 'mine'
          ? 'Failed to load your support requests'
          : 'Failed to load support reports';
        setError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeView, statusFilter, typeFilter, viewFilter, refreshKey]);

  const selectView = (viewId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', viewId);
    setSearchParams(next, { replace: true });
  };

  const handleCreated = () => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'mine');
    setSearchParams(next, { replace: true });
    setRefreshKey((value) => value + 1);
  };

  // Update report status
  const handleStatusChange = async (reportId, newStatus) => {
    try {
      await updateSupportReport(reportId, { status: newStatus });
      // Update local state
      setReports(
        reports.map(report => (report.id === reportId ? { ...report, status: newStatus } : report))
      );
      toast.success('Status updated successfully');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  // Toggle release note flag
  const toggleReleaseNote = async (reportId, currentValue) => {
    try {
      await updateSupportReport(reportId, { isReleaseNote: !currentValue });
      // Update local state
      setReports(
        reports.map(report =>
          report.id === reportId ? { ...report, isReleaseNote: !currentValue } : report
        )
      );
      toast.success(`Report ${!currentValue ? 'marked as' : 'removed from'} release notes`);
    } catch (err) {
      console.error('Error updating release note flag:', err);
      toast.error('Failed to update release note status');
    }
  };

  const filteredReports = reports;

  // Format date for display
  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';

    // If timestamp is a Firestore timestamp, convert it
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">{queueTitle}</h1>
              <p className="text-sm text-gray-600 mt-1">{activeMeta?.description}</p>
            </div>
            {canFile ? (
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                + Report a Problem
              </button>
            ) : null}
          </div>

          {views.length > 1 ? (
            <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Support views">
              {views.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  role="tab"
                  aria-selected={view.id === activeView}
                  onClick={() => selectView(view.id)}
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    view.id === activeView
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>
          ) : null}

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div>
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="typeFilter" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id="typeFilter"
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {showSignalBridgeFilter && (
            <div>
              <label htmlFor="viewFilter" className="block text-sm font-medium text-gray-700">
                Queue
              </label>
              <select
                id="viewFilter"
                value={viewFilter}
                onChange={e => setViewFilter(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="">All reports</option>
                <option value="signal_bridge">Escalated / Signal Bridge</option>
              </select>
            </div>
            )}

            <div className="flex items-end">
              <button
                type="button"
                onClick={() => setRefreshKey((value) => value + 1)}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div
              className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Loading spinner */}
          {loading ? (
            <div className="flex justify-center my-8">
              <LoadingSpinner />
            </div>
          ) : error ? null : (
            <>
              {/* Reports table */}
              {filteredReports.length > 0 ? (
                <div className="overflow-x-auto shadow-md rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Category
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Date
                        </th>
                        {showReleaseNotes ? (
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Release Note
                        </th>
                        ) : (
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Escalation
                        </th>
                        )}
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredReports.map(report => (
                        <tr key={report.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{report.title}</div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {report.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${
                              report.type === 'bug'
                                ? 'bg-red-100 text-red-800'
                                : report.type === 'feature'
                                  ? 'bg-green-100 text-green-800'
                                  : report.type === 'feedback'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}
                            >
                              {supportCategoryLabel(report.type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {canManageQueue ? (
                            <select
                              value={report.status}
                              onChange={e => handleStatusChange(report.id, e.target.value)}
                              className="block w-full pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                              aria-label={`Status for ${report.title}`}
                            >
                              {statusOptions
                                .filter(option => option.value)
                                .map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                            </select>
                            ) : (
                              <span className="text-sm text-gray-700">{supportStatusLabel(report.status)}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(report.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {!canManageQueue ? (
                              <span className="text-sm text-gray-600">
                                {report.escalationStatus === 'escalated' ? 'Escalated' : '—'}
                              </span>
                            ) : showReleaseNotes ? (
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={report.isReleaseNote || false}
                                onChange={() => toggleReleaseNote(report.id, report.isReleaseNote)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-500">Release Note</span>
                            </div>
                            ) : (
                              <span className="text-sm text-gray-600">
                                {report.escalationStatus === 'escalated' ? 'Escalated' : 'Tenant'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => navigate(activeView === 'mine'
                                ? `${ROUTES.MY_REPORT_DETAIL}/${report.id}`
                                : `/support/detail/${report.id}`)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">{supportEmptyMessage(activeView)}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {formOpen ? (
        <SupportFormModal
          isOpen
          onClose={() => setFormOpen(false)}
          onCreated={handleCreated}
        />
      ) : null}
    </MainLayout>
  );
};

export default SupportReportsPage;
